import {
  useAppInsightsContext,
  useTrackMetric,
} from '@microsoft/applicationinsights-react-js';
import { type GetStaticProps } from 'next';
import { type FC, type ReactNode } from 'react';

import { BoldTerm } from '../../components/legal/BoldTerm';
import { InternalLink } from '../../components/legal/InternalLink';
import { SupportLink } from '../../components/legal/SupportLink';
import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../components/page/Layout';

const TITLE = 'Privacy Policy';
const META_DESCRIPTION = 'Leland Privacy Policy';

const SPACING_CLASS = 'my-4';
const UNDERLINE_CLASS = 'underline underline-offset-2';

const TOLL_FREE_NUMBER = '(385) 707-0730';

const PrivacyPolicy: NextPageWithLayout = () => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'PrivacyPolicy');
  return (
    <div className="container-def px-4 py-8 lg:p-20">
      <h1 className="mb-12 text-5xl font-semibold">Legal</h1>

      <h2 className="text-3xl font-semibold uppercase">
        LELAND PRIVACY NOTICE
      </h2>

      <p className={SPACING_CLASS}>
        <i>Last Updated: April 2025</i>
      </p>

      <p className={SPACING_CLASS}>
        Leland Inc. (<BoldTerm>Leland</BoldTerm>, <BoldTerm>we</BoldTerm>,{' '}
        <BoldTerm>us</BoldTerm>, <BoldTerm>our</BoldTerm>) respects your
        privacy. This privacy notice (<BoldTerm>Privacy Notice</BoldTerm>) has
        been adopted on a company-wide basis to guide how we process the
        information that you provide to us through the Leland website, our user
        portal accessible through www.joinleland.com, and services accessible
        through the user portal (collectively, the <BoldTerm>Services</BoldTerm>
        ). Capitalized terms not otherwise defined herein have the meaning given
        such terms in our{' '}
        <InternalLink
          href="/legal/terms"
          text="terms of service"
          id="privacy_policy--terms_of_service"
        />
        .
      </p>
      <p className={SPACING_CLASS}>
        This Privacy Notice does not apply to apps, websites, products, or
        services provided by companies that we do not own or control or to
        individuals that we do not employ or manage.
      </p>
      <p className={`${SPACING_CLASS} ${UNDERLINE_CLASS}`}>
        This Privacy Notice does not apply to any personal information you share
        with your coach or your client outside of the Services. You must use
        your own discretion when sharing personal information about yourself or
        anyone else with your coach or client.
      </p>
      <p className={SPACING_CLASS}>
        Protections provided for processing of personal information do not apply
        to information that does not relate to an identified or identifiable
        individual or to personal information or data rendered anonymous in such
        a manner that the individual is not or no longer identifiable, (
        <BoldTerm>Anonymized Information</BoldTerm>). We may process Anonymized
        Information for our own purposes in any manner and without attribution
        or compensation to any person.
      </p>
      <p className={SPACING_CLASS}>
        We reserve the right to change, modify, add, or remove portions of this
        Privacy Notice. We will post notice of modifications to this Privacy
        Notice on this page. You must check this page periodically for changes.
        Changes will become effective immediately but will not apply
        retroactively. If you do not agree to the modified Privacy Notice you
        should immediately discontinue your use of the Services.
      </p>

      <h3 className="subheading-text mt-6">Lawful Basis for Processing</h3>
      <p className={SPACING_CLASS}>
        Many jurisdictions require that we disclose to you the lawful basis for
        our processing of your personal information. We do that throughout this
        Privacy Notice. In general, our lawful basis for processing your
        personal information is based on your specific consent or your contract
        with us.
      </p>
      <p className={SPACING_CLASS}>
        By accessing or using any of the Services or by otherwise interacting
        with us online, you consent to our processing of your personal
        information as described in this Privacy Notice. If our processing of
        your personal information is based on your consent, you may withdraw
        your consent at any time, and we will cease processing your personal
        information. However, in some cases, this may result in your inability
        to receive partial or full access to the Services, and your withdrawal
        of consent does not limit our ability to process Anonymized Information
        for use by us in connection with our legitimate business efforts in the
        future. In addition, your withdrawal of consent does not prevent us from
        processing personal information if we are required to do so by
        applicable law or to preserve legal claims. It also doesn&rsquo;t
        prevent us from processing personal information that is processed
        pursuant to a different lawful basis. For example, if you give your
        consent for us to process your personal information, but we are also
        required by law to keep your personal information, that separate
        &ldquo;lawful basis&rdquo; will still apply, even if you withdraw your
        consent.
      </p>
      <p className={SPACING_CLASS}>
        When you enter into an agreement with us, either by accessing the
        Services, by executing an agreement in hard copy or by clicking &ldquo;I
        Accept&rdquo; or similar language online, we will process your personal
        information for the purposes of fulfilling the terms of our contract
        with you. In that case, our processing of your personal information is
        based on the contract, so your withdrawal of consent will only be
        effective after the purposes for processing that personal information
        have been fulfilled and after we no longer have a legal obligation to
        keep that personal information.
      </p>
      <p className={SPACING_CLASS}>
        In all cases, we will comply with applicable law and we will cease
        processing your personal information after the legal right, obligation,
        or other lawful basis expires.
      </p>

      <h3 className="subheading-text mt-6">Information We Process</h3>
      <p className={SPACING_CLASS}>
        You do not need to proactively provide any personal information to view
        our website, but you will have to provide required personal information
        to fully use our coach and client matching services. You will not be
        able to fully utilize the Services if you choose to not provide personal
        information that is required for the Services.
      </p>

      <p className={SPACING_CLASS}>
        <b className="font-semibold">Information You Give to Us.</b> You may
        voluntarily give us certain personal information when you register for
        an account and use the Services.
      </p>
      <p className={SPACING_CLASS}>
        As a client, personal information that we may process from you includes:
      </p>
      <List
        items={[
          'Name',
          'Photos',
          'Contact information (email, phone number, address)',
          'Education information (GMAT, GRE, test scores, GPA, high school(s) and/or college(s) attended, schools applying to)',
          'Payment information',
          'LinkedIn profile',
          'Location',
          'Account username & password',
          'Career information (companies worked for, roles, positions held, awards, other achievements)',
          'Additional information about your career, educational or other life background, goals, and plans',
          'Other User Content (including content processed for recordings or Transcriptions)',
        ]}
      />
      <p className={SPACING_CLASS}>
        As a coach, personal information that we may process from you includes:
      </p>
      <List
        items={[
          'Name',
          'Photos',
          'Contact information (email, phone number, address)',
          'Education information (GMAT, GRE, test scores, GPA, high school(s) and/or college(s) attended, schools applying to)',
          'Payment information',
          'Location',
          'LinkedIn profile',
          'Account username & password',
          'Career information (companies worked for, roles, positions held, awards, other achievements)',
          'Video describing your background and services',
          'Additional information about your career, educational, or other life background, goals, and plans',
          'Calendar and availability data for the purposes of scheduling with clients',
          'Other User Content (including content processed for recordings or Transcriptions)',
        ]}
      />

      <p className={SPACING_CLASS}>
        <b className="font-semibold">Information We Process Automatically.</b>{' '}
        Whenever you access or use the Services, we may automatically process
        information, some of which may constitute personal information, from
        your web browser, which may include: the device you use to access or use
        the Services, your IP address, platform types, preferences you submit
        and preferences that are generated based on the data you submit, and
        number of clicks.
      </p>

      <h3 className="subheading-text mt-6">
        Why Process Your Personal Information
      </h3>
      <p className={SPACING_CLASS}>
        <b className="font-semibold">Personal Information.</b> We may process
        your personal information:
      </p>
      <List
        items={[
          'to provide you the Services;',
          'for the specific purpose for which that information was processed;',
          'to evaluate your profile as a coach;',
          'to evaluate your profile as a client;',
          'to match you with a coach as a client or to match you with a client as a coach;',
          'to address your customer service or technical support questions;',
          'to alert you of new products or services, features, or enhancements;',
          'to improve our Services;',
          'to train, refine, or develop artificial intelligence models;',
          'as otherwise permitted by law; and/or',
          'for any other purpose with your consent.',
        ]}
      />

      <h3 className="subheading-text mt-6">
        When We May Disclose Your Personal Information
      </h3>
      <p className={SPACING_CLASS}>
        We may transfer or disclose your personal information for the purposes
        described below:
      </p>
      <List
        items={[
          <>
            <b className="font-semibold">With Coaches and Clients.</b> We
            transfer your personal information with coaches if you&rsquo;re a
            client and with clients if you&rsquo;re a coach. This enables
            coaches and clients to work more effectively together and serves to
            match clients to the right coach.
          </>,
          <>
            <b className="font-semibold">With Your Consent.</b> We may transfer
            your personal information when we have your consent. You can revoke
            your consent at any time by providing notice to us at{' '}
            <SupportLink />.
          </>,
          <>
            <b className="font-semibold">Service Providers.</b> We may employ
            other companies and people to perform tasks on our behalf and we may
            need to transfer your personal information with them to provide
            Services to you. Unless we tell you differently, they do not have
            any right to use your personal information beyond what is necessary
            to assist us.
          </>,
          <>
            <b className="font-semibold">
              Protection of Leland&rsquo;s Business.
            </b>{' '}
            We may also disclose your personal information at the request of law
            enforcement or government agencies or in response to subpoenas,
            court orders, or other legal process to establish, protect, or
            exercise our legal or other rights or to defend against a legal
            claim or as otherwise required or allowed by law. We may disclose
            your personal information in order to protect the rights, property,
            or safety of a user or any other person. We may disclose your
            personal information to investigate or prevent a violation by you of
            any contractual or other relationship with us or your illegal or
            harmful activity.
          </>,
          <>
            <b className="font-semibold">Business Transfers.</b> In the event we
            undergo a business transaction such as a merger, acquisition by
            another company, or sale of all or a portion of our assets, your
            personal information may be among the assets transferred. Any
            acquirer of our assets may continue to process your personal
            information as set forth in this Privacy Notice.
          </>,
          <>
            <b className="font-semibold">
              Third-Party Services that are embedded in Leland products.
            </b>{' '}
            Leland uses Whereby to power our video platform (including to
            provide Transcriptions), Stripe to power our payment processing,
            Google & Outlook to power our calendar syncing, Chorus.ai to support
            our sales operations. Information, videos, recordings, or other
            personal information may be processed through these third-party
            services in order to provide coaches and clients a quality
            experience on the Services.
          </>,
        ]}
      />

      <h3 className="subheading-text mt-6">Cookies</h3>
      <p className={SPACING_CLASS}>
        We use cookies to operate and administer the Services, process usage
        data on the Services, and improve your experience on it. A
        &quot;cookie&quot; is a piece of information sent to your browser by a
        website you visit. Cookies can be stored on your computer for different
        periods of time. Some cookies expire after a certain amount of time, or
        upon logging out (session cookies), others survive after your browser is
        closed until a defined expiration date set in the cookie (as determined
        by the third party placing it), and help recognize your computer when
        you open your browser and browse the Internet again (persistent
        cookies).
      </p>
      <p className={SPACING_CLASS}>
        We use Google Analytics, a web analytics service provided by Google,
        Inc. (<BoldTerm>Google</BoldTerm>). Google Analytics uses cookies to
        help us analyze how users use the Services and enhance your experience
        when you use the Services. For more information on how Google uses this
        data, go to{' '}
        <a
          className="link text-leland-primary"
          href="https://www.google.com/policies/privacy/partners/"
          target="_blank"
          rel="noreferrer"
          id="privacy_policy--view_google_privacy_policy"
        >
          www.google.com/policies/privacy/partners/
        </a>
        .
      </p>
      <p className={SPACING_CLASS}>
        We and our third-party service providers may use cookies or other
        tracking technologies to process information about your browsing
        activities over time and across different websites following your use of
        the Services and use that information to send targeted advertisements.
      </p>
      <p className={SPACING_CLASS}>
        Please refer to your web browser&rsquo;s documentation for information
        on how to receive a notification when you are receiving a new cookie and
        how to turn cookies off.
      </p>
      <p className={SPACING_CLASS}>
        Please note that if you limit the ability of websites to set cookies,
        you may be unable to access certain parts of the Services and you may
        not be able to benefit from the full functionality of the Services.
      </p>
      <p className={SPACING_CLASS}>
        Advertising networks may process personal information through the use of
        cookies. Most advertising networks offer you a way to opt out of
        targeted advertising. If you would like to find out more information,
        please visit the Network Advertising Initiative&rsquo;s online resources
        at{' '}
        <a
          className="link text-leland-primary"
          href="http://www.networkadvertising.org"
          target="_blank"
          rel="noreferrer"
          id="privacy_policy--view_network_advertising_initiative"
        >
          http://www.networkadvertising.org
        </a>{' '}
        and follow the opt-out instructions there.
      </p>

      <h3 className="subheading-text mt-6">Marketing Communications</h3>
      <p className={SPACING_CLASS}>
        You have the right at any time to prevent us from contacting you for
        marketing purposes. You can opt-out of promotional communications by
        following the unsubscribe instructions provided in each promotional
        email that we send to you. We may continue to send you administrative
        emails; for example, periodic updates to our Privacy Notice, even if you
        indicate that you no longer wish to receive promotional email from us.
        Please note that if you do opt-out of receiving marketing-related emails
        from us, we may still send you messages for administrative or other
        purposes directly relating to your use of the Services, and you cannot
        opt-out from receiving those messages while continuing to use the
        Services.
      </p>

      <h3 className="subheading-text mt-6">
        Retention And Modification Of Your Personal Information
      </h3>
      <p className={SPACING_CLASS}>
        You can correct, update, or remove your personal information if it
        changes by updating your account. You can delete your account through
        your account preferences.
      </p>

      <h3 className="subheading-text mt-6">Links</h3>
      <p className={SPACING_CLASS}>
        We may post links to third-party websites or services in the Services.
        We are not responsible or liable for any content, advertising, products,
        or other materials on or available from third-party websites or for any
        privacy or other practices of the third parties operating those
        websites.
      </p>

      <h3 className="subheading-text mt-6 uppercase">
        Your Rights Regarding Your Data
      </h3>
      <p className={SPACING_CLASS}>
        Under applicable data privacy, protection, and other laws, you have
        certain rights related to your access and control of your personal
        information. Such rights may include the following:
      </p>
      <List
        ordered
        items={[
          'The right to access, correct, update, or request deletion of your personal information.',
          'The right to object to processing or restrict the processing of your personal information. Please note that if you exercise this right, it may limit or eliminate our ability to provide you the Services.',
          'The right to request portability of your personal information.',
          'The right to not be subject to a decision based solely on automated processing, including profiling, known as Automatic Decision Making.',
          'The right to submit a complaint to any applicable regulatory authority about our processing activities.',
          'The right to opt-out of us sharing (as defined in the CPRA) your personal information, including for direct marketing purposes, subject to certain legal exceptions.',
          'The right to limit use, disclosure, and restrict sensitive personal information (as defined in the CPRA).',
        ]}
      />
      <p className={SPACING_CLASS}>
        We may use additional processes to verify your identity before we reveal
        or delete any of your personal information, including two-factor or
        two-step authentication measures to ensure we can identify you.
      </p>
      <p className={SPACING_CLASS}>
        This list may not include all of your rights under applicable laws. If
        you believe you have additional rights, please contact us using the
        methods in this Privacy Notice.
      </p>
      <p className={SPACING_CLASS}>
        Further, although we currently do not process personal information
        without consent, if we at any time in the future process personal
        information without your express consent, you may opt-out or withdraw
        consent at any time.
      </p>
      <p className={SPACING_CLASS}>
        Please note that exercising any of the above rights may limit or
        eliminate our ability to provide you the Services. If so, we may
        terminate the Services due to such requests.
      </p>
      <p className={SPACING_CLASS}>
        We will try to comply with your request(s) as soon as reasonably
        practicable. Upon receipt of your written request, we will provide you
        with a copy of your information, although in certain limited
        circumstances we may not be able to make all relevant information
        available to you, such as where that information also pertains to
        another user. In such circumstances we will provide reasons for the
        denial to you upon request.
      </p>
      <p className={SPACING_CLASS}>
        To exercise any of these rights, or if you have any questions about our
        processing of your personal information, please contact us at{' '}
        <SupportLink /> or at {TOLL_FREE_NUMBER}.
      </p>

      <h4 className="mt-6 text-xl font-semibold">
        A. Privacy for EU/UK Residents
      </h4>
      <p className={SPACING_CLASS}>
        The Regulation (EU) 2016/679 (General Data Protection Regulation) made
        effective in Europe on May 25, 2018 (<BoldTerm>GDPR</BoldTerm>) requires
        that we clearly describe to data subjects the data we process and how we
        process that data. This Privacy Policy does that and if you have any
        questions for us regarding our data processing, please contact us at{' '}
        <SupportLink />.
      </p>
      <p className={SPACING_CLASS}>
        We are based in the United States. By accessing or using the Services or
        otherwise providing information to us, you understand that your
        information will be subject to processing, transfer, and storage in and
        to the United States. The data protection and other laws of the United
        States and other countries might not be as comprehensive as those in
        your country.
      </p>
      <p className={SPACING_CLASS}>
        Due to the nature of our Services, we typically act as a
        &ldquo;Processor&rdquo; as defined under the GDPR. If you believe that
        this role should be defined differently, please contact us at{' '}
        <SupportLink />.
      </p>
      <p className={SPACING_CLASS}>
        Pursuant to the GDPR, residents of Europe have the right to obtain our
        confirmation of whether we maintain personal information relating to
        them in the United States. If you are a resident of Europe, upon request
        from you, we will provide you with access to the personal information
        that we hold about you. Please contact us if you have any questions.
      </p>
      <p className={SPACING_CLASS}>
        Further, if you are a resident of the United Kingdom (
        <BoldTerm>UK</BoldTerm>), to the extent the GDPR as incorporated into UK
        law pursuant to s.3 of the European Union (Withdrawal Act) 2018 (as
        amended, the <BoldTerm>UK GDPR</BoldTerm>), is different than the GDPR,
        we will follow all supplemental requirements under the UK GDPR and you
        have all rights as a UK citizen under the UK GDPR.
      </p>

      <h4 className="mt-6 text-xl font-semibold">
        B. Privacy for California Residents
      </h4>
      <p className={SPACING_CLASS}>
        California adopted the California Consumer Privacy Act (
        <BoldTerm>CCPA</BoldTerm>), which took effect at the beginning of 2020
        and the California Privacy Rights Act (<BoldTerm>CPRA</BoldTerm>), which
        fully took effect January 1, 2022. We comply with the requirements of
        the CCPA and CPRA to the extent they apply to us.
      </p>
      <p className={SPACING_CLASS}>
        If you are a California resident, you may request to exercise your
        rights for any personal information we have processed in the 12 months
        prior to your request. Such request covers any categories, sources,
        purposes, and, if applicable, third parties to whom we share the
        personal information. Further, you can exercise any of your rights free
        of discrimination, for example, we cannot increase the price of the
        Services or decrease the quality of the Services because you exercise
        your rights.
      </p>
      <p className={SPACING_CLASS}>
        Due to the nature of our Services, we typically act as a &ldquo;service
        provider&rdquo; as defined under the CCPA and CPRA. If you believe that
        this role should be defined differently, please contact us at{' '}
        <SupportLink /> or at our toll-free number: {TOLL_FREE_NUMBER}.
      </p>
      <p className={SPACING_CLASS}>
        For more information, please direct your questions to us at{' '}
        <SupportLink /> or you can call our toll-free number at{' '}
        {TOLL_FREE_NUMBER}.
      </p>
      <p className={SPACING_CLASS}>
        <b className="font-semibold">Shine the Light.</b> If you are a
        California resident who provides personal information in obtaining
        products or services for personal, family or household use, California
        Civil Code Section 1798.83 (&quot;Shine the Light Law&quot;) permits you
        to request in writing and obtain from us, once per calendar year, data
        about the personal information we shared, if any, with other third
        parties for their own direct marketing purposes. To make such a request,
        please contact us at <SupportLink /> and write &quot;Request for
        California Privacy Data&quot; for the subject of your message. We will
        send you a reply e-mail within 30 days containing the requested data
        once we confirm your request. Not all of the shared personal information
        is covered by the Shine the Light Law requirements and therefore only
        personal information which is covered will be included in our response.
      </p>
      <p className={SPACING_CLASS}>
        <b className="font-semibold">Consumer Rights Notice.</b> Under
        California Civil Code Section 1789.3, Leland is required to provide
        California residents with the following specific consumer rights
        information:
      </p>
      <List
        items={[
          'This website is owned and operated by Leland Inc.',
          'Unless otherwise expressly stated, this website is provided without charge.',
        ]}
      />
      <p className={SPACING_CLASS}>
        To file a complaint regarding this website or to receive further
        information regarding use of this website, please contact us via email
        at <SupportLink /> or send us a letter at the mailing address listed
        below. You also may contact the Complaint Assistance Unit of the
        Division of Consumer Services of California&rsquo;s Department of
        Consumer Affairs in writing at 400 R Street, Suite 1080, Sacramento,
        California 95814 or by telephone at (916) 445-1254 or (800) 952-5210.
      </p>

      <h4 className="mt-6 text-xl font-semibold">
        C. Privacy for Other Jurisdictions
      </h4>
      <p className={SPACING_CLASS}>
        We strive to comply with all data protection and privacy laws in
        applicable jurisdictions, to the extent such laws apply to us and our
        Services. We strive to be transparent about our data processing
        activities and have disclosed our practices throughout this Privacy
        Policy. If you have any questions about your rights under any applicable
        data protection and privacy laws, please contact us at <SupportLink />{' '}
        or you can call our toll-free number at {TOLL_FREE_NUMBER}.
      </p>

      <h3 className="subheading-text mt-6">
        How We Protect Your Personal Information
      </h3>
      <p className={SPACING_CLASS}>
        We take reasonable steps to maintain the security of your personal
        information; however, no data transmission over the Internet can be
        guaranteed to be completely secure. Accordingly, we cannot ensure or
        warrant the security of any information that you transmit or provide to
        us, so you do so at your own risk.
      </p>
      <p className={SPACING_CLASS}>
        <b className="font-semibold">Do Not Track.</b> The Services currently do
        not respond to &quot;Do Not Track&quot; (<BoldTerm>DNT</BoldTerm>)
        signals and operates as described in this Privacy Notice whether or not
        a DNT signal is received. If we do respond to DNT signals in the future,
        we will update this Privacy Notice to describe how we do so.
      </p>

      <h3 className="subheading-text mt-6">Children&rsquo;s Privacy</h3>
      <p className={SPACING_CLASS}>
        We do not knowingly process information from children under the age of
        13 (<BoldTerm>Child</BoldTerm> or <BoldTerm>Children</BoldTerm>), nor do
        we solicit personal information from Children. If you are a Child,
        please do not send any personal information about yourself to us. If you
        are the parent or legal guardian of a Child, we urge you to instruct
        your Child to never give out their real names, address, phone numbers,
        or any other personal information on the Internet without your
        permission.
      </p>
      <p className={SPACING_CLASS}>
        If we learn that we have processed personal information from a Child, we
        will delete that information as soon as possible. Please email us at{' '}
        <SupportLink /> if you believe we have processed personal information
        from a Child.
      </p>

      <h3 className="subheading-text mt-6">Contact Us</h3>
      <p className={SPACING_CLASS}>
        If you have any questions or would like further clarification about the
        Services, any of our products or services, or this Privacy Notice,
        please e-mail us at <SupportLink />.
      </p>
    </div>
  );
};

const List: FC<{ items: ReactNode[]; ordered?: boolean }> = ({
  items,
  ordered,
}) => {
  const ListComponent = ordered ? 'ol' : 'ul';

  return (
    <ListComponent
      className={`${SPACING_CLASS} ${ordered ? 'list-decimal' : 'list-disc'} space-y-1.5 pl-12`}
    >
      {items.map((item, i) => (
        <li key={i}>
          <p>{item}</p>
        </li>
      ))}
    </ListComponent>
  );
};

PrivacyPolicy.getLayout = (page, { pageContentData }) => (
  <Layout
    title={TITLE}
    description={META_DESCRIPTION}
    canonicalLink="/legal/privacy-policy"
    pageContentData={pageContentData}
  >
    {page}
  </Layout>
);

export const getStaticProps: GetStaticProps<{
  pageContentData: Possible<LayoutDataProps>;
}> = async () => {
  const layoutCategoriesHeaderData = await getStaticLayoutQuery();

  return { props: { pageContentData: layoutCategoriesHeaderData } };
};

export default PrivacyPolicy;
